import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Compass, Heart, User, BarChart2, LogOut, Menu, X, Building2, Shield, KeyRound, Settings, CreditCard, ChevronDown, Radar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agent-connect-93/artifacts/akzw3wcz_goldielocks-logo-2048.png";

export default function Navbar() {
  const { user, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [leadCount, setLeadCount] = useState(0);
  const [prospectCount, setProspectCount] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Lead + prospect badges for agent/seller
  useEffect(() => {
    if (!user || !['agent', 'seller'].includes(user.role)) { setLeadCount(0); setProspectCount(0); return; }
    authFetch(`${API}/api/agent/stats`)
      .then(r => r.ok ? r.json() : {})
      .then(d => { setLeadCount(d.lead_count || 0); setProspectCount(d.prospect_count || 0); })
      .catch(() => {});
  }, [user]); // eslint-disable-line

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const NavLink = ({ to, icon: Icon, label, testId, badge }) => (
    <Link
      to={to}
      data-testid={testId}
      onClick={() => setMobileOpen(false)}
      className={`relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
        isActive(to)
          ? 'bg-emerald-500 text-white'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
      }`}
    >
      <Icon size={16} />
      {label}
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-white text-xs font-black flex items-center justify-center"
          style={{ background: '#EF4444', fontSize: '10px' }}
          data-testid={`badge-${testId}`}>
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </Link>
  );

  const buyerLinks = [
    { to: '/discover',  icon: Compass, label: 'Discover',  testId: 'nav-discover' },
    { to: '/map',       icon: MapPin,  label: 'Map',       testId: 'nav-map' },
    { to: '/saved',     icon: Heart,   label: 'Saved',     testId: 'nav-saved' },
    { to: '/profile',   icon: User,    label: 'My Profile', testId: 'nav-profile' },
  ];

  const agentLinks = [
    { to: '/agent',            icon: BarChart2,  label: 'Dashboard',  testId: 'nav-agent-dash' },
    { to: '/agent/listings',   icon: Building2,  label: 'Listings',   testId: 'nav-agent-listings' },
    { to: '/agent/leads',      icon: User,       label: 'Leads',      testId: 'nav-agent-leads', badge: leadCount },
    { to: '/agent/prospects',  icon: Radar,      label: 'Prospects',  testId: 'nav-agent-prospects', badge: prospectCount },
  ];

  const sellerLinks = [
    { to: '/seller',          icon: BarChart2, label: 'Dashboard',  testId: 'nav-seller-dash' },
    { to: '/seller/listings', icon: KeyRound,  label: 'My Home',    testId: 'nav-seller-listings' },
    { to: '/seller/leads',    icon: User,      label: 'Buyer Leads', testId: 'nav-seller-leads', badge: leadCount },
  ];

  const adminLinks = [
    { to: '/admin', icon: Shield, label: 'Admin', testId: 'nav-admin-dash' },
  ];

  const links = user?.role === 'buyer' ? buyerLinks
    : user?.role === 'agent' ? agentLinks
    : user?.role === 'seller' ? sellerLinks
    : adminLinks;

  const roleColor = user?.role === 'buyer' ? 'bg-emerald-100 text-emerald-700'
    : user?.role === 'agent' ? 'bg-blue-100 text-blue-700'
    : user?.role === 'seller' ? 'bg-orange-100 text-orange-700'
    : 'bg-purple-100 text-purple-700';

  const billingPath = user?.role === 'agent' ? '/agent' : '/seller';

  // Avatar initials
  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  const avatarBg = user?.role === 'buyer' ? '#10B981'
    : user?.role === 'agent' ? '#0A192F'
    : user?.role === 'seller' ? '#F97316'
    : '#7C3AED';

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Goldielocks"
              className="h-10 w-auto rounded-xl object-contain"
              style={{ maxWidth: '130px' }} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {user && links.map(link => <NavLink key={link.to} {...link} />)}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(o => !o)}
                  data-testid="nav-profile-btn"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-100 transition-all duration-200"
                >
                  {user.profile_photo ? (
                    <img src={user.profile_photo} alt={user.name}
                      className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm" />
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                      style={{ background: avatarBg }}>
                      {initials}
                    </div>
                  )}
                  <div className="text-left">
                    <div className="text-xs font-bold text-slate-800 leading-tight">{user.name}</div>
                    <div className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-semibold capitalize ${roleColor}`}>
                      {user.role}
                    </div>
                  </div>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown */}
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden z-50"
                    data-testid="profile-dropdown">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-bold text-slate-800 truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <button onClick={() => { setProfileOpen(false); navigate('/settings'); }}
                        data-testid="nav-settings"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <Settings size={15} className="text-slate-400" />
                        Profile Settings
                      </button>
                      {['agent', 'seller'].includes(user.role) && (
                        <button onClick={() => { setProfileOpen(false); navigate(billingPath); }}
                          data-testid="nav-billing"
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                          <CreditCard size={15} className="text-slate-400" />
                          Billing & Subscription
                        </button>
                      )}
                    </div>
                    <div className="py-1 border-t border-slate-100">
                      <button onClick={handleLogout} data-testid="nav-logout"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors">
                        <LogOut size={15} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login"
                  className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors">
                  Log in
                </Link>
                <Link to="/register?role=seller" data-testid="nav-list-home"
                  className="px-4 py-2 text-sm font-bold transition-all duration-200 hover:opacity-90 rounded-lg"
                  style={{ color: '#0A192F', border: '1px solid #CBD5E1' }}>
                  List my home
                </Link>
                <Link to="/register" data-testid="nav-register"
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:opacity-90"
                  style={{ background: '#0A192F' }}>
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
            onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 pt-2 border-t border-slate-100 space-y-1">
            {user ? (
              <>
                <div className={`flex items-center gap-2 px-3 py-2 ${roleColor} rounded-lg mb-2`}>
                  <span className="text-xs font-semibold capitalize">{user.role}</span>
                  <span className="text-sm font-medium">— {user.name}</span>
                </div>
                {links.map(link => <NavLink key={link.to} {...link} />)}
                <button onClick={() => { setMobileOpen(false); navigate('/settings'); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 w-full transition-all">
                  <Settings size={16} />Profile Settings
                </button>
                <button onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-red-500 hover:bg-red-50 w-full transition-all">
                  <LogOut size={16} />Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-all">
                  Log in
                </Link>
                <Link to="/register?role=seller" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-all"
                  style={{ border: '1px solid #E2E8F0' }}>
                  List my home
                </Link>
                <Link to="/register" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                  style={{ background: '#0A192F' }}>
                  Get Started
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
